<template>
    <div class="stripe-source-bank-account" :class="{loading: loading}">
        <fieldset>
            <form @submit.prevent="saveBankAccount" accept-charset="UTF-8" novalidate="novalidate">
                <legend>Add Bank Account</legend>

                <p>
                    <b>Note:</b> After adding your bank account details the account will need to be verified before it
                    can be charged. Two small deposits will be made to the account. This process can take from 1 - 3
                    days. Once the deposits are available in your statement please enter the amounts to verify the
                    account.
                </p>

                <div class="row">
                    <div class="col-sm-9 form-group has-feedback"
                         :class="{'has-error': errors.has('account_holder_name')}">
                        <label for="account-holder-name">Account Holder Name</label>

                        <input id="account-holder-name"
                               v-validate="'required'"
                               v-model="bankAccount.account_holder_name"
                               name="account_holder_name"
                               type="text"
                               class="form-control"
                               aria-required="true">

                        <span v-show="errors.has('account_holder_name')" class="help-block">
                            {{ errors.first('account_holder_name') }}
                        </span>
                    </div>

                    <div class="col-sm-3 form-group has-feedback"
                         :class="{'has-error': errors.has('account_holder_type')}">
                        <label for="account-holder-type">Account Type</label>

                        <select id="account-holder-type"
                                v-validate="'required'"
                                v-model="bankAccount.account_holder_type"
                                name="account_holder_type"
                                class="form-control"
                                aria-required="true">
                            <option value="individual">Individual</option>
                            <option value="company">Company</option>
                        </select>

                        <span v-show="errors.has('account_holder_type')" class="help-block">
                            {{ errors.first('account_holder_type') }}
                        </span>
                    </div>
                </div>

                <div class="row">
                    <div class="form-group has-feedback col-sm-6"
                         :class="{'has-error': errors.has('account_number')}">
                        <label for="account-number">Account Number</label>

                        <input id="account-number"
                               v-validate="'required'"
                               v-model="bankAccount.account_number"
                               name="account_number"
                               type="text"
                               class="form-control"
                               aria-required="true">

                        <span v-show="errors.has('account_number')" class="help-block">
                            {{ errors.first('account_number') }}
                        </span>
                    </div>

                    <div class="form-group has-feedback col-sm-6"
                         :class="{'has-error': errors.has('routing_number')}">
                        <label for="routing-number">Routing Number</label>

                        <input id="routing-number"
                               v-validate="'required|length:9'"
                               v-model="bankAccount.routing_number"
                               name="routing_number"
                               type="text"
                               class="form-control"
                               aria-required="true">

                        <span v-show="errors.has('routing_number')" class="help-block">
                            {{ errors.first('routing_number') }}
                        </span>
                    </div>

                    <div class="form-group has-feedback col-xs-12"
                         v-if="errors.has('bank_account_token')"
                         :class="{'has-error': errors.has('bank_account_token')}">

                        <span v-show="errors.has('bank_account_token')" class="help-block">
                            {{ errors.first('bank_account_token') }}
                        </span>
                    </div>
                </div>

                <hr>

                <div class="form-group">
                    <button type="submit" class="btn btn-success" :disabled="loading">
                        Save Bank Account
                    </button>
                </div>
            </form>
        </fieldset>
    </div>
</template>

<script>
    /**
     * Note: Cannot be nested within a form element with a non-https action
     */
    export default {
        props: {
            afterSave: {
                default: () => {
                    return function(){
                    };
                }
            },

            modalId: {
                default: function(){
                    return Math.random().toString();
                },
                type: String
            },
        },

        created(){
            this.init();
        },

        data(){
            return {
                loading: false,

                style: {
                    base: {
                        color: '#282828',
                        fontFamily: '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSmoothing: 'antialiased',
                        fontSize: '16px',
                        '::placeholder': {
                            color: '#282828'
                        },
                        lineHeight: '26px',
                        padding: '8px'
                    },

                    invalid: {
                        color: '#C70039',
                        iconColor: '#900C3F'
                    }
                },

                bankAccount: {
                    account_holder_type: 'individual'
                },

                stripe: null,
            };
        },

        computed: {
            form(){
                var form = document.createElement( 'form' );
                form.setAttribute( 'method', 'POST' );
                form.setAttribute( 'action', '/stripeCustomer/sources' );

                var hiddenField = document.createElement( 'input' );
                hiddenField.setAttribute( 'type', 'hidden' );
                hiddenField.setAttribute( 'name', '_method' );
                hiddenField.setAttribute( 'value', 'POST' );

                var token = $( 'meta[name="csrf-token"]' ).attr( 'content' );
                var tokenField = document.createElement( 'input' );
                tokenField.setAttribute( 'type', 'hidden' );
                tokenField.setAttribute( 'name', '_token' );
                tokenField.setAttribute( 'value', token );

                form.appendChild( tokenField );
                form.appendChild( hiddenField );

                return form;
            },
        },

        watch: {},

        methods: {
            init(){
                var promise = Promise.resolve();

                this.loading = true;

                promise
                    .then( () => {
                        return this.loadStripe();
                    } )
                    .finally( () => {
                        this.loading = false;
                    } )
                    .catch( this.$root.handleErrors );
            },

            loadStripe(){
                this.stripe = Stripe( process.env.MIX_STRIPE_PUBLIC_KEY );
            },

            saveBankAccount(){
                this.loading = true;
                return this.$validator.validateAll()
                    .then( ( valid ) => {
                        if( valid ){
                            return this.stripe
                                .createToken( 'bank_account', {
                                    country: 'US',
                                    currency: 'usd',
                                    routing_number: this.bankAccount.routing_number,
                                    account_number: this.bankAccount.account_number,
                                    account_holder_name: this.bankAccount.account_holder_name,
                                    account_holder_type: this.bankAccount.account_holder_type,
                                } )
                                .then( ( result ) => {
                                    if( result.error ){
                                        this.errors.add( {
                                            field: 'bank_account_token',
                                            msg: result.error.message
                                        } );
                                    }
                                    else {
                                        return this.postSource( result.token );
                                    }
                                } )
                                .finally( () => {
                                    this.loading = false;
                                } )
                                .catch( this.$root.handleErrors );
                        } else {
                            this.loading = false;
                        }
                    } )
                    .finally( () => {
                        this.loading = false;
                    });
            },

            postSource( token ){
                var form = this.form;
                var sourceTokenField = document.createElement( 'input' );
                sourceTokenField.setAttribute( 'name', 'Source[token]' );
                sourceTokenField.setAttribute( 'value', token.id );
                form.appendChild( sourceTokenField );

                document.body.appendChild( form );
                form.submit();
            },

            resetData(){
                Object.assign( this.$data, this.$options.data() );
            },
        }
    }
</script>
